/** Premium / Category 页使用的分类 id，与 i18n category.* 一致 */
export const PREMIUM_CATEGORY_IDS = [
  'romance',
  'urban',
  'revenge',
  'family',
  'sweet_love',
  'tragic_love',
  'comeback',
  'rebirth',
  'costume',
  'ceo',
  'cute_baby',
  'time_travel',
  'fantasy',
  'republic_era',
  'period_drama',
  'suspense',
  'system',
  'war_god',
  'love',
  'modern',
  'harem',
  'xianxia',
  'divine_doctor'
]

export function normalizePlaylistsResponse (data) {
  const payload = data && !Array.isArray(data) ? data : { dramas: data }
  const list =
    payload?.dramas ??
    payload?.playlists ??
    payload?.list ??
    (Array.isArray(data) ? data : [])
  const dramas = (Array.isArray(list) ? list : []).map(normalizePlaylistDrama)
  return {
    dramas,
    total: payload?.total ?? null,
    page: payload?.page ?? null,
    pageSize: payload?.pageSize ?? null
  }
}

/** playlists 条目字段与首页卡片字段对齐 */
export function normalizePlaylistDrama (item) {
  if (!item || typeof item !== 'object') return item
  return {
    ...item,
    dramaId: item.dramaId ?? item.tid,
    coverUrl: item.coverUrl ?? item.coverImage ?? '',
    total: item.total ?? item.episodeCount ?? 0
  }
}

/** Discovery See All：已实现 playlists 筛选的分组 */
export const DISCOVERY_PLAYLIST_GROUPS = {
  english_original: { source: 'selfUploadEN', language: 'en' },
  subtitled_series: { source: 'selfUpload', language: 'en' },
  motion_comics: { source: 'selfUploadComic', language: 'en' },
  vengeful_rise: { tag: 'vengeful_rise' },
  fall_for_ceo: { tag: 'fall_for_ceo' },
  werewolf_alpha: { tag: 'werewolf_alpha' },
  he_cheated: { tag: 'he_cheated' },
  new_releases: { onlineTimeAfterDays: 7 },
  // 首页顶部频道（原声剧 / AI漫剧 / 译制剧）横滑分组：仅按 tag 过滤
  en_contract: { tag: 'en_contract' },
  en_secret_lives: { tag: 'en_secret_lives' },
  en_secret_baby: { tag: 'en_secret_baby' },
  en_mafia: { tag: 'en_mafia' },
  en_sweet: { tag: 'en_sweet' },
  comic_wolf: { tag: 'comic_wolf' },
  comic_fantasy: { tag: 'comic_fantasy' },
  comic_contract: { tag: 'comic_contract' },
  comic_secret_lives: { tag: 'comic_secret_lives' },
  comic_secret_baby: { tag: 'comic_secret_baby' },
  sub_revenge: { tag: 'sub_revenge' },
  sub_betrayal: { tag: 'sub_betrayal' },
  sub_sweet: { tag: 'sub_sweet' }
}

/**
 * 首页顶部频道配置。
 * - `source`：底部「全部」两列列表的筛选条件（分页拉取）。
 * - `groups`：横滑分组，按顺序展示，每组仅按 tag 过滤（pageSize=16，不分页）。
 * 分组标题走 i18n `discovery.<groupId>`。
 */
export const HOME_CHANNELS = {
  originals: {
    source: 'selfUploadEN',
    groups: ['en_contract', 'en_secret_lives', 'en_secret_baby', 'en_mafia', 'en_sweet']
  },
  motionComics: {
    source: 'selfUploadComic',
    groups: ['comic_wolf', 'comic_fantasy', 'comic_contract', 'comic_secret_lives', 'comic_secret_baby']
  },
  subtitled: {
    source: 'selfUpload',
    groups: ['sub_revenge', 'sub_betrayal', 'sub_sweet']
  }
}

export function getHomeChannel (channelId) {
  return HOME_CHANNELS[channelId] || null
}

export function getMidnightTimestampDaysAgo (days) {
  const date = new Date()
  date.setDate(date.getDate() - days)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

export function isDiscoveryPlaylistEnabled (groupId) {
  return Object.prototype.hasOwnProperty.call(DISCOVERY_PLAYLIST_GROUPS, groupId)
}

export function getDiscoveryPlaylistQuery (groupId) {
  const cfg = DISCOVERY_PLAYLIST_GROUPS[groupId]
  if (!cfg) return null
  const query = {}
  if (cfg.source != null) query.source = cfg.source
  if (cfg.tag != null) query.tag = cfg.tag
  if (cfg.language != null) query.language = cfg.language
  if (cfg.onlineTimeAfterDays != null) {
    query.onlineTimeAfter = getMidnightTimestampDaysAgo(cfg.onlineTimeAfterDays)
  }
  return query
}

/** 从首页接口中按出现顺序提取 groupType === 'discovery' 的分组 */
export function extractDiscoveryGroups (groups) {
  if (!Array.isArray(groups)) return []
  return groups
    .filter(
      (g) =>
        g?.groupType === 'discovery' &&
        Array.isArray(g.data) &&
        g.data.length > 0
    )
    .map((g) => ({
      groupId: g.groupId,
      list: g.data
    }))
}

export function extractPremiumCategoryFromHomeGroups (groups) {
  const categoryMap = {}

  groups.forEach((group) => {
    if (group.groupType !== 'category') {
      return
    }
    categoryMap[group.groupId] = group.data
      .slice()
      .sort((a, b) => (b.hotDegree || 0) - (a.hotDegree || 0))
  })

  const categories = PREMIUM_CATEGORY_IDS.filter((id) => categoryMap[id]?.length)

  return { categories, categoryMap }
}

export function normalizeFeedsResponse (data) {
  const list = data?.feeds ?? data?.list ?? (Array.isArray(data) ? data : [])
  return Array.isArray(list) ? list : []
}

export function normalizeHistoryResponse (data) {
  const list = data?.history ?? data?.histories ?? (Array.isArray(data) ? data : [])
  return { history: Array.isArray(list) ? list : [] }
}

export function normalizeFavoritesResponse (data) {
  const list = data?.dramas ?? data?.favorites ?? (Array.isArray(data) ? data : [])
  return { dramas: list }
}

export function buildPlaylistParams ({ page, pageSize, category, language, source, tag, lowestHotDegree, onlineTimeAfter } = {}) {
  const params = {}
  if (page != null) params.page = page
  if (pageSize != null) params.pageSize = pageSize
  if (category != null) params['filter[category]'] = category
  if (language != null) params['filter[language]'] = language
  if (source != null) params['filter[source]'] = source
  if (tag != null) params['filter[tag]'] = tag
  if (lowestHotDegree != null) params['filter[lowestHotDegree]'] = lowestHotDegree
  if (onlineTimeAfter != null) params['filter[onlineTimeAfter]'] = onlineTimeAfter
  return params
}
